/**
 * Cálculo dos indicadores da tela de insights.
 *
 * Separado da UI de propósito: é lógica pura sobre o payload da planilha, sem
 * React e sem efeito colateral. Nada aqui grava — a tela de insights é somente
 * leitura.
 *
 * Decisão importante: os totais são somados a partir das linhas, NUNCA lidos de
 * `planilha.totalGeral`. Esse campo é gravado com o que o cliente envia e não é
 * recalculado no servidor — em planilha real de produção ele consta 0 enquanto
 * as linhas somam mais de R$ 222 mil. Confiar nele mostraria um painel zerado.
 */

import { ItemPlanilha, Planilha, TipoItem } from "@models/index";

/** Tipos de linha que representam um custo real na planilha. */
const TIPOS_COM_VALOR: TipoItem[] = [
  TipoItem.ITEM,
  TipoItem.ITEM_TAXAS,
  TipoItem.ITEM_ACESSORIA,
];

/** Status de cotação que carregam uma proposta com preço utilizável. */
const STATUS_COM_PROPOSTA = ["respondida", "recebida", "reenviada"];

export interface CotacaoNormalizada {
  fornecedorId: string;
  fornecedorNome: string;
  valorUnitario: number;
  status: string;
  /** Total da proposta considerando quantidade e diárias do item. */
  valorTotal: number;
  recusou: boolean;
  /** Horas entre solicitação e resposta. Ausente quando não há as duas datas. */
  horasParaResponder?: number;
  acessos: number;
}

export interface ItemAnalisado {
  id: string;
  numero: string;
  descricao: string;
  tipo: TipoItem;
  grupo: string;
  subgrupo: string;
  quantidade: number;
  diaria: number;
  /** Total efetivo da linha (o que entra no custo do evento). */
  total: number;
  cotacoes: CotacaoNormalizada[];
  /** Cotações com preço, ordenadas da menor para a maior. */
  propostas: CotacaoNormalizada[];
  vencedora?: CotacaoNormalizada;
  maisCara?: CotacaoNormalizada;
  /** Quanto se deixou de gastar em relação à proposta mais cara. */
  economia: number;
  /** Razão entre a maior e a menor proposta. 1 = todas iguais. */
  spread: number;
  recusas: number;
}

export interface FatiaFornecedor {
  nome: string;
  valor: number;
  fracao: number;
}

export interface DesempenhoFornecedor {
  nome: string;
  disputas: number;
  vitorias: number;
  taxaVitoria: number;
  valorGanho: number;
  recusas: number;
  /** Média de horas para responder. Ausente se nunca respondeu com data. */
  horasMedias?: number;
  /** Nunca abriu nenhum link recebido. */
  nuncaAbriu: boolean;
}

export interface Insights {
  evento: string;
  cliente: string;
  local?: string | null;
  periodo?: string | null;

  /** Soma das linhas com valor. Fonte da verdade do painel. */
  custoTotal: number;
  /** O que está gravado na planilha, para sinalizar divergência. */
  custoGravado: number;
  divergenciaDeTotal: boolean;

  economiaTotal: number;
  /** Quanto o pior cenário custaria: custo total + economia. */
  custoPiorCenario: number;
  economiaPercentual: number;

  itens: ItemAnalisado[];
  itensCotaveis: number;
  itensComTresPropostas: number;
  itensSemProposta: number;
  coberturaTresPropostas: number;

  /** Ordenados por total, com percentual acumulado — base do Pareto. */
  pareto: Array<ItemAnalisado & { fracao: number; acumulado: number }>;
  /** Quantos itens bastam para somar 80% do custo. */
  itensAte80Porcento: number;

  concentracao: FatiaFornecedor[];
  /** Maior fatia individual, para o alerta de dependência. */
  maiorFatia?: FatiaFornecedor;

  desempenho: DesempenhoFornecedor[];

  totalCotacoes: number;
  totalRespostas: number;
  totalRecusas: number;
  horasMediaResposta?: number;
  fornecedoresQueNaoAbriram: string[];

  /** Itens cujo spread chama atenção — especificação dúbia ou preço fora da curva. */
  spreadsAtipicos: ItemAnalisado[];
}

/** Aceita o formato sem fuso que a API devolve, tratando o valor como UTC. */
const paraData = (valor?: string | null): Date | null => {
  if (!valor) return null;
  const temFuso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(valor);
  const d = new Date(temFuso ? valor : `${valor}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const horasEntre = (de: Date, ate: Date) =>
  (ate.getTime() - de.getTime()) / 3_600_000;

const nomeDaCotacao = (f: any): string =>
  f?.fornecedor?.nome || f?.fornecedorNome || "Fornecedor sem nome";

function normalizarCotacoes(item: ItemPlanilha): CotacaoNormalizada[] {
  const quantidade = item.quantidade || 1;
  const diaria = item.diaria || 1;

  return (item.fornecedores ?? []).map((f: any) => {
    const status = String(f.status ?? "").toLowerCase().trim();
    const valorUnitario = Number(f.unitario) || 0;

    const solicitacao = paraData(f.dataSolicitacao);
    const resposta = paraData(f.dataResposta);
    let horasParaResponder: number | undefined;
    if (solicitacao && resposta) {
      const h = horasEntre(solicitacao, resposta);
      // Resposta antes da solicitação acontece em cotação reenviada, que mantém
      // a data de resposta anterior. Descartar em vez de reportar negativo.
      if (h >= 0) horasParaResponder = h;
    }

    return {
      fornecedorId: f.fornecedorId,
      fornecedorNome: nomeDaCotacao(f),
      valorUnitario,
      status,
      valorTotal: valorUnitario * quantidade * diaria,
      recusou: status === "rejeitada" || status === "recusada",
      horasParaResponder,
      acessos: Number(f.totalAcessos) || 0,
    };
  });
}

function analisarItem(
  item: ItemPlanilha,
  grupo: string,
  subgrupo: string,
): ItemAnalisado {
  const cotacoes = normalizarCotacoes(item);

  const propostas = cotacoes
    .filter(
      (c) =>
        !c.recusou &&
        c.valorUnitario > 0 &&
        STATUS_COM_PROPOSTA.includes(c.status),
    )
    .sort((a, b) => a.valorUnitario - b.valorUnitario);

  const vencedora = propostas[0];
  const maisCara = propostas[propostas.length - 1];

  // Economia só existe com concorrência: uma proposta única não foi disputada.
  const economia =
    propostas.length > 1 && maisCara && vencedora
      ? maisCara.valorTotal - vencedora.valorTotal
      : 0;

  const spread =
    propostas.length > 1 && vencedora && vencedora.valorUnitario > 0
      ? maisCara!.valorUnitario / vencedora.valorUnitario
      : 1;

  return {
    id: item.id ?? `${item.tipo}-${item.numero}`,
    numero: item.numero ?? "",
    descricao: item.item ?? "",
    tipo: item.tipo,
    grupo,
    subgrupo,
    quantidade: item.quantidade || 1,
    diaria: item.diaria || 1,
    total: Number(item.total) || 0,
    cotacoes,
    propostas,
    vencedora,
    maisCara,
    economia,
    spread,
    recusas: cotacoes.filter((c) => c.recusou).length,
  };
}

function montarConcentracao(itens: ItemAnalisado[]): FatiaFornecedor[] {
  const porFornecedor = new Map<string, number>();

  for (const item of itens) {
    if (!item.vencedora) continue;
    const atual = porFornecedor.get(item.vencedora.fornecedorNome) ?? 0;
    porFornecedor.set(item.vencedora.fornecedorNome, atual + item.vencedora.valorTotal);
  }

  const total = [...porFornecedor.values()].reduce((s, v) => s + v, 0);
  if (total <= 0) return [];

  return [...porFornecedor.entries()]
    .map(([nome, valor]) => ({ nome, valor, fracao: valor / total }))
    .sort((a, b) => b.valor - a.valor);
}

function montarDesempenho(itens: ItemAnalisado[]): DesempenhoFornecedor[] {
  interface Acc {
    disputas: number;
    vitorias: number;
    valorGanho: number;
    recusas: number;
    horas: number[];
    acessosTotais: number;
    envios: number;
  }

  const mapa = new Map<string, Acc>();
  const pegar = (nome: string): Acc => {
    if (!mapa.has(nome)) {
      mapa.set(nome, {
        disputas: 0,
        vitorias: 0,
        valorGanho: 0,
        recusas: 0,
        horas: [],
        acessosTotais: 0,
        envios: 0,
      });
    }
    return mapa.get(nome)!;
  };

  for (const item of itens) {
    for (const c of item.cotacoes) {
      const acc = pegar(c.fornecedorNome);
      acc.disputas += 1;
      acc.envios += 1;
      acc.acessosTotais += c.acessos;
      if (c.recusou) acc.recusas += 1;
      if (c.horasParaResponder !== undefined) acc.horas.push(c.horasParaResponder);
    }
    if (item.vencedora) {
      const acc = pegar(item.vencedora.fornecedorNome);
      acc.vitorias += 1;
      acc.valorGanho += item.vencedora.valorTotal;
    }
  }

  return [...mapa.entries()]
    .map(([nome, a]) => ({
      nome,
      disputas: a.disputas,
      vitorias: a.vitorias,
      taxaVitoria: a.disputas > 0 ? a.vitorias / a.disputas : 0,
      valorGanho: a.valorGanho,
      recusas: a.recusas,
      horasMedias:
        a.horas.length > 0
          ? a.horas.reduce((s, h) => s + h, 0) / a.horas.length
          : undefined,
      nuncaAbriu: a.envios > 0 && a.acessosTotais === 0,
    }))
    .sort((a, b) => b.valorGanho - a.valorGanho || b.disputas - a.disputas);
}

export function calcularInsights(
  planilha: Planilha,
  itensBrutos: ItemPlanilha[],
): Insights {
  // Grupo e subgrupo são posicionais: a linha herda o último cabeçalho acima
  // dela. A planilha é uma lista plana, sem vínculo de pai.
  let grupoAtual = "";
  let subgrupoAtual = "";
  const analisados: ItemAnalisado[] = [];

  for (const linha of itensBrutos) {
    if (linha.tipo === TipoItem.GRUPO) {
      grupoAtual = linha.item ?? "";
      subgrupoAtual = "";
      continue;
    }
    if (linha.tipo === TipoItem.SUBGRUPO) {
      subgrupoAtual = linha.item ?? "";
      continue;
    }
    if (TIPOS_COM_VALOR.includes(linha.tipo)) {
      analisados.push(analisarItem(linha, grupoAtual, subgrupoAtual));
    }
  }

  const custoTotal = analisados.reduce((s, i) => s + i.total, 0);
  const economiaTotal = analisados.reduce((s, i) => s + i.economia, 0);
  const custoPiorCenario = custoTotal + economiaTotal;

  // Só os itens de tipo "item" passam pelo portal do fornecedor. Taxas e
  // despesas acessórias não são cotadas, então não entram na cobertura.
  const cotaveis = analisados.filter((i) => i.tipo === TipoItem.ITEM);
  const itensComTresPropostas = cotaveis.filter((i) => i.propostas.length >= 3).length;
  const itensSemProposta = cotaveis.filter((i) => i.propostas.length === 0).length;

  const comValor = analisados.filter((i) => i.total > 0);
  const ordenados = [...comValor].sort((a, b) => b.total - a.total);
  let acumulado = 0;
  const pareto = ordenados.map((i) => {
    const fracao = custoTotal > 0 ? i.total / custoTotal : 0;
    acumulado += fracao;
    return { ...i, fracao, acumulado };
  });
  const idx80 = pareto.findIndex((p) => p.acumulado >= 0.8);
  const itensAte80Porcento = idx80 >= 0 ? idx80 + 1 : pareto.length;

  const concentracao = montarConcentracao(analisados);
  const desempenho = montarDesempenho(analisados);

  const todasCotacoes = analisados.flatMap((i) => i.cotacoes);
  const horas = todasCotacoes
    .map((c) => c.horasParaResponder)
    .filter((h): h is number => h !== undefined);

  const periodo = [planilha.dataInicio, planilha.dataFim]
    .filter(Boolean)
    .join(" a ");

  return {
    evento: planilha.evento,
    cliente: planilha.clienteNome,
    local: [planilha.localEvento, planilha.ufEvento].filter(Boolean).join(" - ") || null,
    periodo: periodo || null,

    custoTotal,
    custoGravado: Number(planilha.totalGeral) || 0,
    // Tolerância de 1 centavo para não acusar divergência por arredondamento.
    divergenciaDeTotal:
      Math.abs(custoTotal - (Number(planilha.totalGeral) || 0)) > 0.01,

    economiaTotal,
    custoPiorCenario,
    economiaPercentual: custoPiorCenario > 0 ? economiaTotal / custoPiorCenario : 0,

    itens: analisados,
    itensCotaveis: cotaveis.length,
    itensComTresPropostas,
    itensSemProposta,
    coberturaTresPropostas:
      cotaveis.length > 0 ? itensComTresPropostas / cotaveis.length : 0,

    pareto,
    itensAte80Porcento,

    concentracao,
    maiorFatia: concentracao[0],

    desempenho,

    totalCotacoes: todasCotacoes.length,
    totalRespostas: todasCotacoes.filter((c) =>
      STATUS_COM_PROPOSTA.includes(c.status),
    ).length,
    totalRecusas: todasCotacoes.filter((c) => c.recusou).length,
    horasMediaResposta:
      horas.length > 0 ? horas.reduce((s, h) => s + h, 0) / horas.length : undefined,
    fornecedoresQueNaoAbriram: desempenho.filter((d) => d.nuncaAbriu).map((d) => d.nome),

    // 1,8x é o ponto em que a diferença deixa de ser negociação e sugere que os
    // fornecedores entenderam coisas diferentes do mesmo descritivo.
    spreadsAtipicos: analisados
      .filter((i) => i.spread >= 1.8)
      .sort((a, b) => b.spread - a.spread),
  };
}
